import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { profileApi, invitationApi, linkedAccountsApi } from './endpoints';
import { useAuth } from './contexts/AuthenticationContext';
import { useActiveContext } from './contexts/ActiveOrgContext';
import { useImpersonation } from './contexts/ImpersonationContext';
import { isSystemAdmin, isInstructor } from './helpers';

export type { UserProfile, InvitationInfo, ProfileMembership, AcceptInvitationResponse, LinkedAccount } from './endpoints';

// When impersonating, ignore the Auth0 user's claims — those belong to the
// real admin, not the impersonated target. /me reflects the target (the API
// client sends X-Impersonate-User-Id), so trust the profile only.
export function useIsSystemAdmin(): boolean {
  const { user } = useAuth();
  const { data: profile } = useProfile();
  const { target } = useImpersonation();
  if (target) return isSystemAdmin(profile);
  return isSystemAdmin(user) || isSystemAdmin(profile);
}

export function useIsInstructor(): boolean {
  const { user } = useAuth();
  const { data: profile } = useProfile();
  const { target } = useImpersonation();
  if (target) return isInstructor(profile);
  return isInstructor(user) || isInstructor(profile);
}

export function useActiveOrg(): { orgId: number | undefined; role: string | undefined } {
  const { data: profile } = useProfile();
  const { active } = useActiveContext();
  const activeOrgId = active.kind === 'org' ? active.orgId : undefined;
  const orgId = activeOrgId ?? profile?.memberships?.[0]?.organization?.id;
  const role = profile?.memberships?.find((m) => m.organization?.id === orgId)?.role;
  return { orgId, role };
}

const PENDING_TOKEN_KEY = 'wicklander_pending_invitation';

export function useProfile() {
  const { isAuthenticated } = useAuth();
  // Diverges from the global QueryClient defaults on purpose: profile is the
  // identity/permissions source of truth, so refetch on focus and retry 3x.
  return useQuery({
    queryKey: ['profile'],
    queryFn: profileApi.get,
    enabled: isAuthenticated,
    retry: 3,
    refetchOnWindowFocus: true,
  });
}

// "Default user" = the plain learner role. Admins and instructors don't get
// shipping nudges — materials only ship to learners, and staff profiles are
// managed elsewhere. `profile_complete` (User#profile_complete? on the API)
// requires first/last name plus a full shipping address.
export function useNeedsProfileCompletion(): boolean {
  const { data: profile } = useProfile();
  return !!profile && profile.role === 'user' && !profile.profile_complete;
}

// Post-checkout handoff: a page that handles a Stripe success redirect flips
// `armed` to true, and once the profile query resolves (often still loading
// right after the full-page redirect back from Stripe) this sends first-time
// buyers with incomplete profiles to /complete-profile. The enrollment sync
// mutation the caller fired keeps running after unmount — TanStack mutations
// and their invalidations live on the queryClient, not the component.
export function useCompleteProfileAfterCheckout(armed: boolean, returnTo: string) {
  const navigate = useNavigate();
  const { data: profile } = useProfile();
  const needsCompletion = useNeedsProfileCompletion();

  useEffect(() => {
    if (!armed || !profile || !needsCompletion) return;
    navigate('/complete-profile', { state: { fromCheckout: true, returnTo } });
  }, [armed, profile, needsCompletion, navigate, returnTo]);
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: profileApi.update,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['profile'] }),
  });
}

export function useInvitationDetails(token: string | null) {
  return useQuery({
    queryKey: ['invitation', token],
    queryFn: () => {
      if (!token) throw new Error('useInvitationDetails requires a token');
      return invitationApi.get(token);
    },
    enabled: !!token,
    retry: false,
  });
}

export function useAcceptInvitation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: invitationApi.accept,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
  });
}

export function useLinkedAccounts() {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ['linked_accounts'],
    queryFn: linkedAccountsApi.list,
    enabled: isAuthenticated,
  });
}

export function useAddLinkedAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: linkedAccountsApi.create,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['linked_accounts'] }),
  });
}

export function useRemoveLinkedAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: linkedAccountsApi.destroy,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['linked_accounts'] }),
  });
}

export function savePendingInvitationToken(token: string) {
  localStorage.setItem(PENDING_TOKEN_KEY, token);
}

export function getPendingInvitationToken(): string | null {
  return localStorage.getItem(PENDING_TOKEN_KEY);
}

export function clearPendingInvitationToken() {
  localStorage.removeItem(PENDING_TOKEN_KEY);
}
